<template>
  <div class="main-content invoice-create-page">
    <form @submit.prevent="submitForm">
      <div class="page-header pt-5">
        <h4 class="page-title">{{ $t('payments.title') }}</h4>
        <div class="page-actions" />
      </div>

      <!-- Row payment info -->
      <div class="row invoice-input-group">
        <!-- Provider name -->
        <div class="col invoice-input">
          <label>{{ $t('payments.name') }} <span class="text-danger"> * </span></label>
          <base-input
            v-model="paymentForm.name"
            :invalid="$v.paymentForm.name.$error"
            icon="user"
            type="text"
          />
          <div v-if="$v.paymentForm.name.$error">
            <span v-if="!$v.paymentForm.name.maxLength" class="text-danger">{{ $t('validation.required') }}</span>
          </div>
        </div>
        <!-- Payment date -->
        <div class="col invoice-input">
          <label>{{ $t('payments.payment_date') }} <span class="text-danger"> * </span></label>
          <base-date-picker
            v-model="paymentForm.payment_date"
            :invalid="$v.paymentForm.payment_date.$error"
            :calendar-button="true"
            calendar-button-icon="calendar"
            @change="$v.paymentForm.payment_date.$touch()"
          />
          <span v-if="$v.paymentForm.payment_date.$error && !$v.paymentForm.payment_date.required" class="text-danger mt-1"> 
            {{ $t('validation.required') }}
          </span>
        </div>
        <!-- Payment number -->
        <div class="col invoice-input">
          <label>{{ $t('payments.payment_number') }}</label>
          <base-input
            v-model="paymentForm.payment_number"
            icon="hashtag"
            type="text"
          />
        </div>
      </div>
      <div class="row mt-4">
        <!-- Provider RFC -->
        <div class="col invoice-input">
          <label>{{ $t('payments.rfc') }} <span class="text-danger"> * </span></label>
          <base-input
            v-model="paymentForm.rfc"
            :invalid="$v.paymentForm.rfc.$error"
            icon="user"
            type="text"
          />
          <div v-if="$v.paymentForm.rfc.$error">
            <span v-if="!$v.paymentForm.rfc.maxLength" class="text-danger">{{ $t('validation.required') }}</span>
          </div>
        </div>
        <div class="col invoice-input" />
        <div class="col invoice-input" />
      </div>

      <!-- Row items -->
      <table class="item-table mt-5">
        <colgroup>
          <col style="width: 40%;">
          <col style="width: 10%;">
          <col style="width: 15%;">
          <col style="width: 15%;">
        </colgroup>
        <thead class="item-table-header">
          <tr>
            <th class="text-left">
              <span class="column-heading item-heading">
                {{ $tc('items.item',2) }}
              </span>
            </th>
            <th class="text-center">
              <span class="column-heading">
                {{ $t('invoices.item.quantity') }}
              </span>
            </th>
            <th class="text-left">
              <span class="column-heading">
                {{ $t('invoices.item.price') }}
              </span>
            </th>
            <th class="text-right">
              <span class="column-heading amount-heading">
                {{ $t('invoices.item.total') }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody class="item-body">
          <create-payment-item
            v-for="(item, index) in paymentForm.items"
            :key="item.id"
            :index="index"
            :item-data="item"
            :items="paymentForm.items"
            @remove="removeItem"
            @update="updateItem"
            @itemValidate="checkItemsData"
          />
        </tbody>
      </table>
      <div class="add-item-action" @click="addItem">
        <font-awesome-icon icon="shopping-basket" class="mr-2"/>
        {{ $t('invoices.add_item') }}
      </div>

      <!-- Row notes, subtotal, discount, total -->
      <div class="row mt-4 invoice-foot">
        <!-- Notes -->
        <div class="col">
          <label>{{ $t('invoices.notes') }}</label>
          <base-text-area
            v-model="paymentForm.notes"
            rows="3"
            cols="50"
            @input="$v.paymentForm.notes.$touch()"
          />
          <div v-if="$v.paymentForm.notes.$error">
            <span v-if="!$v.paymentForm.notes.maxLength" class="text-danger">{{ $t('validation.notes_maxlength') }}</span>
          </div>
        </div>

        <div class="col" />

        <div class="col">
          <div class="invoice-total">

            <!-- Subtotal -->
            <div class="section">
              <label class="invoice-label">{{ $t('payments.sub_total') }}</label>
              <label class="invoice-amount">
                <div v-html="$utils.formatMoney(subtotal, defaultCurrency)" />
              </label>
            </div>

            <!-- Discount -->
            <div class="section">
              <label class="invoice-label">{{ $t('invoices.discount') }}</label>
              <div class="invoice-amount">
                <div
                  class="btn-group discount-drop-down"
                  role="group"
                >
                  <base-input
                    v-model="paymentForm.discount"
                    :invalid="$v.paymentForm.discount_val.$error"
                    input-class="item-discount"
                    @input="$v.paymentForm.discount_val.$touch()"
                  />
                  <v-dropdown :show-arrow="false">
                    <button
                      slot="activator"
                      type="button"
                      class="btn item-dropdown dropdown-toggle"
                      data-toggle="dropdown"
                      aria-haspopup="true"
                      aria-expanded="false"
                    >
                      {{ paymentForm.discount_type == 'fixed' ? defaultCurrency.symbol : '%' }}
                    </button>
                    <v-dropdown-item>
                      <a class="dropdown-item" href="#" @click.prevent="selectPercentage">
                        {{ $t('general.percentage') }}
                      </a>
                    </v-dropdown-item>
                    <v-dropdown-item>
                      <a class="dropdown-item" href="#" @click.prevent="selectFixed">
                        {{ $t('general.fixed') }}
                      </a>
                    </v-dropdown-item>
                  </v-dropdown>
                </div>
              </div>
            </div>

            <!-- Base -->
            <div class="section">
              <label class="invoice-label">{{ $t('payments.base') }}</label>
              <label class="invoice-amount">
                <div v-html="$utils.formatMoney(base, defaultCurrency)" />
              </label>
            </div>

            <!-- Tax list -->
            <div>
              <tax
                v-for="(tax, index) in paymentForm.taxes"
                :index="index"
                :total="base"
                :key="tax.id"
                :tax="tax"
                :taxes="paymentForm.taxes"
                :currency="defaultCurrency"
                :total-tax="totalTax"
                @remove="removePaymentTax"
                @update="updateTax"
              />
            </div>

            <!-- Tax selector -->
            <div>
              <base-popup ref="taxModal" class="tax-selector">
                <div slot="activator" class="float-right">
                  + {{ $t('invoices.add_tax') }}
                </div>
                <tax-select-popup 
                  :taxes="[]"
                  :show-search-bar="false"
                  :show-add-new-tax="false"
                  @select="onSelectTax"
                />
              </base-popup>
            </div>

            <!-- Total -->
            <div class="section border-top mt-3">
              <label class="invoice-label">{{ $t('invoices.total') }}</label>
              <label class="invoice-amount">
                <div v-html="$utils.formatMoney(totalAmount, defaultCurrency)" />
              </label>
            </div>

          </div>
        </div>
      </div>

      <!-- Row action button -->
      <div class="row">
        <div class="col-4 offset-8 center-content">
          <base-button
            :loading="isLoading"
            :disabled="isLoading"
            icon="save"
            color="theme"
            class="invoice-action-btn"
            type="submit"
          >
            {{ $t('payments.save_payment') }}
          </base-button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { required, between, maxLength } from 'vuelidate/lib/validators';
import moment from 'moment';
import draggable from 'vuedraggable';
import { validationMixin } from 'vuelidate';
import { mapActions, mapGetters } from 'vuex';
import CreatePaymentItem from './CreatePaymentItem';
import Tax from '../invoices/InvoiceTax';
import PaymentStub from '../../stub/payment';
import TaxStub from '../../stub/tax';
import Guid from 'guid';

export default {
  components: {
    draggable,
    CreatePaymentItem,
    Tax,
  },
  mixins: [validationMixin],
  data: () => ({
    paymentForm: {
      name: 'Público en general',
      rfc: 'XAXX010101000',
      customer_id: null,
      payment_date: '',
      payment_number: '',
      subtotal: 0,
      discount: 0,
      discount_type: 'percentage',
      discount_val: 0,
      notes: '',
      base: 0,
      total: 0,
      items: [
        {
          id: Guid.raw(),
          ...PaymentStub,
        }
      ],
      tax_amount: 0,
      taxes: [],
    },
    isLoading: false,
  }),
  computed: {
    ...mapGetters('currency', [
      'defaultCurrency'
    ]),
    ...mapGetters('CurrentCustomer', [
      'currentCustomer',
    ]),
    base () {
      return this.subtotal - this.discountVal;
    },
    discountVal () {
      if (this.paymentForm.discount == 0) {
        return 0;
      }
      if (this.paymentForm.discount_type === 'percentage') {
        return this.subtotal * (Number(this.paymentForm.discount) / 100);
      } else {
        return Number(this.paymentForm.discount) * 100;
      }
    },
    subtotal () {
      return this.paymentForm.items.reduce((accumulator, item) => {
        return accumulator + ((item.price * 100) * item.quantity);
      }, 0);
    },
    totalAmount () {
      return this.base + this.taxAmount - this.retentionAmount;
    },
    totalTax () {
      return this.paymentForm.tax_amount - this.paymentForm.retention_amount;
    },
    taxAmount () {
      const taxes = this.paymentForm.taxes.filter(tax => tax.tax_type == 'tax');
      
      return taxes.reduce((accumulator, tax) => {
        return accumulator + tax.amount;
      }, 0);
    },
    retentionAmount () {
      const taxes = this.paymentForm.taxes.filter(tax => tax.tax_type == 'retention');
      
      return taxes.reduce((accumulator, tax) => {
        return accumulator + tax.amount;
      }, 0);
    },
  },
  watch: {
  },
  validations: function () {
    return {
      paymentForm: {
        name: {
          required,
        },
        rfc: {
          required,
        },
        payment_date: {
          required,
        },
        discount_val: {
          between: between(0, this.subtotal)
        },
        notes: {
          maxLength: maxLength(255)
        },
      }
    };
  },
  created () {
    this.paymentForm.payment_date = moment().subtract(1, 'month').toISOString()
  },
  mounted () {
    if (!this.currentCustomer) {
      this.$router.push('/general/customers')
    }
  },
  methods: {
    ...mapActions('payment', [
      'addPayment',
    ]),
    addItem () {
      this.paymentForm.items.push({
        id: Guid.raw(),
        ...PaymentStub,
      });
    },
    changeForm () {
      for (let i = 0; i < this.paymentForm.taxes.length; i++) {
        const tax = this.paymentForm.taxes[i];
        this.paymentForm.taxes[i].amount = (this.base * tax.percent) / 100;
      }
    },
    checkItemsData (index, isValid) {
      this.paymentForm.items[index].valid = isValid
    },
    checkValid () {
      this.$v.paymentForm.$touch()
      window.hub.$emit('checkItems')
      let isValid = true
      this.paymentForm.items.forEach((item) => {
        if (!item.valid) {
          isValid = false
        }
      })
      if (this.$v.paymentForm.$invalid === false && isValid === true) {
        return true;
      }
      return false;
    },
    createPayment () {
      this.isLoading = true;
      const data = {
        ...this.paymentForm,
        payment_date: moment(this.paymentForm.payment_date).format('DD/MM/YYYY'),
        customer_id: this.currentCustomer.id,
        subtotal: this.subtotal,
        base: this.base,
        total: this.totalAmount,
        discount_val: this.discountVal,
        tax_amount: this.taxAmount - this.retentionAmount,
      };
      data['taxes'] = this.paymentForm.taxes.map((tax) => {
        return tax.tax_type_id;
      });
      this.addPayment(data)
        .then((response) => {
          if (response.data.success) {
            window.toastr['success'](this.$t('payments.created_message'));
            this.isLoading = false;
            this.$router.push('/general/payments');
          }
        })
        .catch((err) => {
          this.isLoading = false;
          window.toastr['error'](err.response.data.error)
        })
    },
    onSelectTax (selectedTax) {
      const existsTax = this.paymentForm.taxes.filter((tax) => tax.tax_type_id == selectedTax.id).length;
      if (existsTax >= 1) {
        window.toastr['warning'](this.$t('validation.you_cant_add_same_tax'));
        this.$refs.taxModal.close();
        return;
      }

      let amount = 0
      if (this.base && selectedTax.percent) {
        amount = (this.base * selectedTax.percent) / 100
      }

      this.paymentForm.taxes.push({
        ...TaxStub,
        id: Guid.raw(),
        name: selectedTax.name,
        percent: selectedTax.percent,
        compound_tax: selectedTax.compound_tax,
        tax_type_id: selectedTax.id,
        tax_type: selectedTax.tax_type,
        amount
      })

      this.changeForm();
      this.$refs.taxModal.close();
    },
    removeItem (index) {
      this.paymentForm.items.splice(index, 1);
    },
    removePaymentTax (index) {
      this.paymentForm.taxes.splice(index, 1)
    },
    submitForm () {
      if (!this.checkValid()) {
        return false
      }

      this.createPayment();
    },
    selectFixed () {
      if (this.paymentForm.discount_type === 'fixed') {
        return;
      }
      this.paymentForm.discount_val = this.paymentForm.discount * 100;
      this.paymentForm.discount_type = 'fixed';
    },
    selectPercentage () {
      if (this.paymentForm.discount_type === 'percentage') {
        return;
      }
      this.paymentForm.discount_val = (this.subtotal * this.paymentForm.discount) / 100;
      this.paymentForm.discount_type = 'percentage';
    },
    updateItem (data) {
      Object.assign(this.paymentForm.items[data.index], {...data.item})
      this.changeForm();
    },
    updateTax (data) {
      Object.assign(this.paymentForm.taxes[data.index], {...data.item})
    },
  }
}
</script>
